import type { Channel, ChannelType, ChannelStatus } from '@conversia/shared-types';
import { api } from './client';

/** Endpoints de canales. Prefijo: /api/channels */

export interface CreateChannelInput {
  type: ChannelType;
  name: string;
  config?: Record<string, unknown>;
}

export interface ConnectResult {
  status: ChannelStatus;
  qr?: string;
}

export const listChannels = () => api.get<Channel[]>('/api/channels');

export const getChannel = (id: string) => api.get<Channel>(`/api/channels/${id}`);

export const getChannelStatus = (id: string) =>
  api.get<{ status: ChannelStatus }>(`/api/channels/${id}/status`);

export const createChannel = (input: CreateChannelInput) =>
  api.post<Channel>('/api/channels', input);

export const updateChannel = (
  id: string,
  input: { name?: string; config?: Record<string, unknown>; defaultAgentId?: string | null },
) => api.patch<Channel>(`/api/channels/${id}`, input);

export const connectChannel = (id: string) =>
  api.post<ConnectResult>(`/api/channels/${id}/connect`);

export const disconnectChannel = (id: string) =>
  api.post<{ success: boolean }>(`/api/channels/${id}/disconnect`);

export const deleteChannel = (id: string) => api.del<{ success: boolean }>(`/api/channels/${id}`);

/**
 * Quita el candado de simulacro. Exige escribir el nombre exacto del canal:
 * desde ese momento salen mensajes REALES a teléfonos reales.
 */
export const quitarSimulacro = (id: string, confirmacion: string) =>
  api.post<Channel>(`/api/channels/${id}/simulacro/quitar`, { confirmacion });

/** Vuelve a poner el candado. Sin confirmación: frenar es la dirección segura. */
export const ponerSimulacro = (id: string) =>
  api.post<Channel>(`/api/channels/${id}/simulacro/poner`, {});

/**
 * Enciende o apaga los avisos automáticos del canal (recordatorios, llegada,
 * encuestas y avisos internos de sus clientes). La asesora sigue contestando.
 */
export const setAvisosPausados = (id: string, pausados: boolean) =>
  api.post<Channel>(`/api/channels/${id}/avisos`, { pausados });

/** Canal de los avisos sin cliente: el elegido (o null = automático) y el que se usa hoy. */
export interface AvisosGenerales {
  configuradoId: string | null;
  efectivoId: string | null;
}

export const getAvisosGenerales = () => api.get<AvisosGenerales>('/api/channels/avisos-generales');

/** Elige el canal de los avisos generales. `null` vuelve al automático (el más viejo). */
export const setAvisosGenerales = (channelId: string | null) =>
  api.put<AvisosGenerales>('/api/channels/avisos-generales', { channelId });

/**
 * DE QUIÉN ES UNA LÍNEA, y si es de solo lectura.
 *
 * Son dos cosas independientes y se mandan por separado:
 *
 *   · `vigilaUserId` sin pasar (undefined) → el dueño NO SE TOCA. Es lo que
 *     hace la casilla de «solo se lee»: cambia una cosa y deja la otra.
 *   · `vigilaUserId: null` → se quita el dueño, porque alguien lo pidió.
 *
 * La distinción no es un detalle: mandar `null` sin querer borra el dueño de la
 * línea, y con él se va a quién se le asignan esos clientes y quién los ve.
 */
export const marcarVigilancia = (
  id: string,
  soloVigilancia: boolean,
  vigilaUserId?: string | null,
) =>
  api.post<Channel>(`/api/channels/${id}/vigilancia`, {
    soloVigilancia,
    ...(vigilaUserId !== undefined ? { vigilaUserId } : {}),
  });
